import { decodeAuthToken } from 'util/decodeAuthToken';
import { isCanUseCallSettings } from 'util/getCallType';
import {
    IN_ROOM,
    TOKEN_STORAGE_KEY,
    VCS_CALL_INITIATOR_ID_STORAGE_KEY,
    VCS_JOIN_POLICY,
    VCS_MODERATOR_BYPASS_PREFIX,
    VCS_PAGE_RELOADING_KEY,
    WSSURL_STORAGE_KEY,
} from 'consts';
import { resolveEffectiveJoinPolicy } from 'features/PreJoin/utils/policy/admission';
import { getCallLsInitiatorId } from 'features/PreJoin/utils/callLsData';
import { selectAuthToken, selectIsAuthorized, selectRoomId } from 'state/selectors';
import { selectCallConfig, selectCurrentUser } from 'state/selectors/app';

export const markPageReloading = (): void => {
    sessionStorage.setItem(VCS_PAGE_RELOADING_KEY, '1');
};

export const consumePageReloading = (): boolean => {
    const isReloading = sessionStorage.getItem(VCS_PAGE_RELOADING_KEY) === '1';
    sessionStorage.removeItem(VCS_PAGE_RELOADING_KEY);
    return isReloading;
};

const getModeratorBypassKey = (roomId: string): string => `${VCS_MODERATOR_BYPASS_PREFIX}${roomId}`;

export const persistModeratorBypassForRoom = (roomId: string): void => {
    sessionStorage.setItem(getModeratorBypassKey(roomId), '1');
};

export const clearModeratorBypassForRoom = (roomId: string): void => {
    sessionStorage.removeItem(getModeratorBypassKey(roomId));
};

/**
 * Локальный userId — для embedded из CALL_DATA (messenger), для web из messenger auth-token.
 * НЕ читаем LiveKit JWT из sessionStorage: его может подменить злоумышленник через devtools,
 * что позволит выдать себя за «инициатора» и обойти waiting room (security review).
 */
const getLocalUserId = (): number | null => {
    const embeddedUser = selectCurrentUser();
    if (embeddedUser?.id != null) {
        return embeddedUser.id;
    }

    const authToken = selectAuthToken();
    if (authToken) {
        const decoded = decodeAuthToken(authToken);
        if (decoded?.userId != null) {
            return decoded.userId;
        }
    }

    return null;
};

/** roomName: uuid:userId:groupId:TYPE:... — userId создателя звонка */
const getInitiatorUserIdFromRoomName = (roomId: string): number | null => {
    const parts = roomId.split(':');
    if (parts.length < 2) {
        return null;
    }
    const userId = Number(parts[1]);
    return Number.isFinite(userId) ? userId : null;
};

export const isCallInitiator = (roomId?: string): boolean => {
    const callConfig = selectCallConfig();
    if (callConfig?.isMeInitiatedGroupCall || callConfig?.isMeInitiatedP2pCall) {
        return true;
    }

    const localUserId = getLocalUserId();
    if (localUserId == null) {
        return false;
    }

    const initiatorId = callConfig?.initiatorId;
    if (initiatorId != null && initiatorId === localUserId) {
        return true;
    }

    const resolvedRoomId = roomId ?? callConfig?.roomName ?? selectRoomId();

    // Проверяем sessionStorage. После F5 sessionStorage.clear() его очистит,
    // тогда берём из localStorage — но только если roomName совпадает с текущей комнатой.
    const storedInitiatorId =
        sessionStorage.getItem(VCS_CALL_INITIATOR_ID_STORAGE_KEY) ?? getCallLsInitiatorId(resolvedRoomId ?? '');
    if (storedInitiatorId != null && Number(storedInitiatorId) === localUserId) {
        return true;
    }

    if (!resolvedRoomId) {
        return false;
    }

    const fromRoomName = getInitiatorUserIdFromRoomName(resolvedRoomId);
    return fromRoomName != null && fromRoomName === localUserId;
};

const hasModeratorBypassFlag = (roomId: string): boolean =>
    sessionStorage.getItem(getModeratorBypassKey(roomId)) === '1';

/**
 * Быстрый вход по сохранённому JWT без joinCallByLink (и без waiting room).
 * Для модератора/инициатора после перезагрузки: IN_ROOM сбрасывается в onLeave,
 * но токен в sessionStorage остаётся.
 */
export const canBypassWaitingRoomOnReconnect = (roomId: string): boolean => {
    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const wssUrl = sessionStorage.getItem(WSSURL_STORAGE_KEY);
    if (!token || !wssUrl) {
        return false;
    }

    if (sessionStorage.getItem(IN_ROOM) === '1') {
        return true;
    }

    if (!selectIsAuthorized()) {
        return false;
    }

    // «Все с запросом» — без bypass для инициатора; только модератор, уже побывавший в комнате.
    if (isCanUseCallSettings() && resolveEffectiveJoinPolicy() === VCS_JOIN_POLICY.ALL_WITH_APPROVAL) {
        return hasModeratorBypassFlag(roomId);
    }

    return isCallInitiator(roomId) || hasModeratorBypassFlag(roomId);
};
